import { LocationVarKey } from "../Generated/LocationVarKey.Data";
import {
  FACILITY_AND_SPACE_FEATURES,
  FEATURE_DEFAULT_TEXT,
  FacilityAndSpaceFeatureDef,
  FacilityAndSpaceFeatures,
  findFeatureDef
} from "./CortexFacilityFeatures.Data";

/**
 * Reading and composing `FacilityAndSpaceFeatures` (ENG-3209).
 *
 * A feature is up to three plain location variables (see the def): an on/off flag,
 * a display-name override, and custom wording. Every read site hands the merged
 * org → group → location vars map to `resolveFeature` / `featureDisplayName` and
 * never interprets the strings itself. A unit row carries the tags FMS sync wrote
 * (`unit.features`); compound facility features declare `composedOf`, and the
 * helpers below are the ONLY place the two meet — nothing else may substring-match
 * a unit's tags.
 */

// ─── Variables ──────────────────────────────────────────────────────────────

/** The on/off flag's stored form: "1"/"true" is on; anything else (including unset) is off. */
export function isFeatureOn(raw: string | null | undefined): boolean {
  const v = (raw ?? "").trim().toLowerCase();
  return v === "1" || v === "true";
}

/** Serialized on/off value for writes. */
export function featureOnValue(on: boolean): string {
  return on ? "1" : "0";
}

/** Every variable that belongs to one feature: on/off (switches), name (all), wording (switches). */
export function featureVarKeys(def: FacilityAndSpaceFeatureDef): LocationVarKey[] {
  const keys: LocationVarKey[] = [];
  if (def.cortexVarKey) keys.push(def.cortexVarKey);
  keys.push(def.nameVarKey);
  if (def.botWordingVarKey) keys.push(def.botWordingVarKey);
  return keys;
}

/** Every variable behind a set of features — what a read site fetches before calling `resolveFeature`. */
export function allFeatureVarKeys(defs: readonly FacilityAndSpaceFeatureDef[] = FACILITY_AND_SPACE_FEATURES): LocationVarKey[] {
  return defs.flatMap(featureVarKeys);
}

/** A def that has an on/off switch (facility / both scope). */
export type FeatureSwitchDef = FacilityAndSpaceFeatureDef & { cortexVarKey: LocationVarKey };

export function isFeatureSwitch(def: FacilityAndSpaceFeatureDef): def is FeatureSwitchDef {
  return def.cortexVarKey !== undefined;
}

/** The operator switches (facility / both scope) — the entries with an on/off flag. */
export function featureSwitches(): FeatureSwitchDef[] {
  return FACILITY_AND_SPACE_FEATURES.filter(isFeatureSwitch);
}

/** Look up the switch def owning an on/off var key. */
export function findFeatureBySwitchKey(cortexVarKey: LocationVarKey): FeatureSwitchDef | undefined {
  return featureSwitches().find((f) => f.cortexVarKey === cortexVarKey);
}

// ─── Resolving ──────────────────────────────────────────────────────────────

/** What a consumer needs to render or speak a feature at one location. */
export interface ResolvedFeature {
  /** The on/off flag. Always false for a unit tag (no switch). */
  available: boolean;
  /** Operator override when non-empty, else the catalog label. */
  displayName: string;
  /** Operator wording when non-empty; else the catalog on/off default; else a label-derived sentence. */
  text: string;
  /** The operator's wording alone, "" when none — for sites that append it in parentheses. */
  customText: string;
}

/**
 * Resolve a feature from the merged vars map. This is the SINGLE place the on/name/
 * text rules live — every read site (system prompt, load_location, lookup_info,
 * compare, find_locations, widget card, PM row) calls this rather than reading the
 * variables itself.
 */
export function resolveFeature(def: FacilityAndSpaceFeatureDef, vars: Partial<Record<LocationVarKey, string>> | undefined): ResolvedFeature {
  const available = def.cortexVarKey ? isFeatureOn(vars?.[def.cortexVarKey]) : false;
  const nameOverride = (vars?.[def.nameVarKey] ?? "").trim();
  const displayName = nameOverride || def.label;
  const customText = def.botWordingVarKey ? (vars?.[def.botWordingVarKey] ?? "").trim() : "";
  const defaults = FEATURE_DEFAULT_TEXT[def.key];
  const text =
    customText ||
    (available
      ? (defaults?.on ?? `${def.label} is available at this facility.`)
      : (defaults?.off ?? `${def.label} is not offered at this facility.`));
  return { available, displayName, text, customText };
}

/**
 * The display name for a feature on any Snowy surface: the operator's override from
 * the feature's name variable when set, else the catalog label. Omit `vars` for the label.
 */
export function featureDisplayName(def: FacilityAndSpaceFeatureDef, vars?: Partial<Record<LocationVarKey, string>>): string {
  return resolveFeature(def, vars).displayName;
}

/** The display-name variables behind a set of features — what a unit-picker producer fetches to name its chips. */
export function featureNameVarKeysFor(features: Iterable<FacilityAndSpaceFeatures>): LocationVarKey[] {
  const keys = new Set<LocationVarKey>();
  for (const f of features) {
    const def = findFeatureDef(f);
    if (def) keys.add(def.nameVarKey);
  }
  return [...keys];
}

/** Display name per feature from the merged vars map (operator rename when set, else the catalog label). */
export function featureDisplayNamesFor(
  features: Iterable<FacilityAndSpaceFeatures>,
  vars: Partial<Record<LocationVarKey, string>> | undefined
): Partial<Record<FacilityAndSpaceFeatures, string>> {
  const out: Partial<Record<FacilityAndSpaceFeatures, string>> = {};
  for (const f of features) {
    const def = findFeatureDef(f);
    if (def) out[f] = featureDisplayName(def, vars);
  }
  return out;
}

// ─── Composition ────────────────────────────────────────────────────────────

const KNOWN_VALUES: ReadonlySet<string> = new Set(Object.values(FacilityAndSpaceFeatures));

/** Drop anything in a stored tag list that isn't a known enum value (defensive: the column is free `text[]`). */
export function knownFeatures(tags: readonly string[] | null | undefined): FacilityAndSpaceFeatures[] {
  const out: FacilityAndSpaceFeatures[] = [];
  for (const tag of tags ?? []) {
    if (KNOWN_VALUES.has(tag) && !out.includes(tag as FacilityAndSpaceFeatures)) out.push(tag as FacilityAndSpaceFeatures);
  }
  return out;
}

/** Does this unit's tag list satisfy the feature — directly, or through the feature's `composedOf`? */
export function unitHasFeature(tags: readonly FacilityAndSpaceFeatures[], feature: FacilityAndSpaceFeatures): boolean {
  if (tags.includes(feature)) return true;
  const composed = findFeatureDef(feature)?.composedOf;
  if (!composed) return false;
  if (!composed.all.every((t) => tags.includes(t))) return false;
  if (composed.any && composed.any.length > 0 && !composed.any.some((t) => tags.includes(t))) return false;
  return true;
}

/**
 * A unit's stored tags plus every compound whose `composedOf` the tags satisfy.
 * `["rv parking", "covered"]` → `["rv parking", "covered", "covered rv parking"]`.
 * Order: stored tags first (as stored), then compounds in catalog order.
 */
export function expandUnitFeatures(tags: readonly string[] | null | undefined): FacilityAndSpaceFeatures[] {
  const known = knownFeatures(tags);
  const out = [...known];
  for (const def of FACILITY_AND_SPACE_FEATURES) {
    if (!def.composedOf || out.includes(def.key)) continue;
    if (unitHasFeature(known, def.key)) out.push(def.key);
  }
  return out;
}

// ─── Lookup ─────────────────────────────────────────────────────────────────

function normalizeFeatureText(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/**
 * Resolve free text (an LLM tool argument, a filter value) to a feature def by
 * key or label, ignoring case, spacing, and punctuation. `"drive-up"`,
 * `"Drive Up"`, and `"drive_up"` all resolve to `FacilityAndSpaceFeatures.DriveUp`.
 */
export function resolveFeatureByText(input: string | null | undefined): FacilityAndSpaceFeatureDef | undefined {
  if (typeof input !== "string") return undefined;
  const normalized = normalizeFeatureText(input);
  if (!normalized) return undefined;
  const exact = FACILITY_AND_SPACE_FEATURES.find((f) => normalizeFeatureText(f.key) === normalized || normalizeFeatureText(f.label) === normalized);
  if (exact) return exact;
  // Close variants an LLM or a filter produces: "climate-controlled" → "climate control";
  // "drive up units" → "drive up". The input must contain a whole key, or a multi-word input
  // must sit whole inside a label — a single bare word ("storage", "rv") never resolves.
  const multiWord = normalized.includes("_");
  return FACILITY_AND_SPACE_FEATURES.find((f) => {
    const key = normalizeFeatureText(f.key);
    const label = normalizeFeatureText(f.label);
    return normalized.includes(key) || (multiWord && label.includes(normalized));
  });
}
